/* ============================================================================
   os-cart-upsell.js — in-cart upgrade nudges (drawer + /cart).
   Added 2026-09-18 at Lauren's request: the product grid is now a single
   one-click 12-pack add, and the upgrade decisions live here in the cart.

   Two nudges, per ONE-TIME line (never on a line that is already a subscription):

     1. 24-PACK UPGRADE — if the line is a 12-pack and the product also sells a
        24-pack, offer to swap it. Savings are real and stated against the
        honest baseline: two 12-packs. "Save $X vs two 12-Packs." Only shown
        when that number is positive.

     2. SUBSCRIBE & SAVE — if the product has a subscription twin, swap the
        one-time line for the twin in place (Lauren 2026-09-20: "replace the one
        time with the subscription product"). Same pack size, same quantity, and
        the shopper picks the delivery cadence in the nudge itself — objection #1
        on this store is being locked into a subscription (21.6% of cancels), so
        the cadence is still chosen, never assumed. Falls back to a link to the
        twin's page if its plans can't be read.

   GOVERNING RULES honoured (see specs/cart.md, rules/05):
     - The purchase path never depends on this script; it only adds nudges.
     - Mounted INSIDE .cart__contents (the scrolling area), so it shortens the
       item list rather than pushing the checkout button toward the fold.
     - No invented urgency, no false numbers. When a product lookup fails, the
       nudge for that line is simply not shown.
   ============================================================================ */

import CartStoreDefault from 'cart-store';

(function () {
  'use strict';
  if (window.__osCartUpsell) { return; }
  window.__osCartUpsell = true;

  var ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  /* --- cart module (add/refresh); raw fetch is the fallback ---------------- */
  /* FIXED 2026-10-02: this used a dynamic import('cart-store'). Shopify's asset
     minifier rewrites that into require(), which does not exist in the browser,
     so the store never loaded and the drawer did not re-render after a
     Subscribe or 24-pack swap (the cart changed, the drawer kept showing the old
     line until reopened). Now a static import; the file is an ES module, loaded
     with type="module" from layout/theme.liquid after the importmap. */
  function cartModule() { return Promise.resolve(CartStoreDefault || null); }

  /* --- helpers ------------------------------------------------------------- */
  function money(cents) {
    if (typeof cents !== 'number' || isNaN(cents)) { return ''; }
    var s = (cents / 100).toFixed(2);
    if (s.slice(-3) === '.00') { s = s.slice(0, -3); }
    return '$' + s;
  }
  function esc(s) {
    return String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;')
      .replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }
  function is12(t) { return /(^|\D)12(\D|$)/.test(String(t || '')); }
  function is24(t) { return /(^|\D)24(\D|$)/.test(String(t || '')); }

  /* Subscription twins, matched by title (rules/05). */
  function subUrlFor(title) {
    var t = (title || '').toLowerCase();
    if (t.indexOf('sockeye') !== -1) { return '/products/subscribe-save-wild-alaskan-sockeye-salmon-fillets-6oz'; }
    if (t.indexOf('variety') !== -1 || t.indexOf('gift box') !== -1) { return '/products/subscribe-save-premium-fish-variety-gift-box-assorted-fillets'; }
    if (t.indexOf('black cod') !== -1 || t.indexOf('sablefish') !== -1) { return '/products/subscription-wild-alaskan-black-cod-sablefish-fillets-6oz'; }
    return null;
  }

  var productCache = {};
  function getProduct(handle) {
    if (Object.prototype.hasOwnProperty.call(productCache, handle)) { return Promise.resolve(productCache[handle]); }
    return fetch(ROOT + 'products/' + encodeURIComponent(handle) + '.js', { credentials: 'same-origin' })
      .then(function (r) { if (!r.ok) { throw new Error('bad'); } return r.json(); })
      .then(function (p) { productCache[handle] = p; return p; })
      .catch(function () { productCache[handle] = null; return null; });
  }

  /* --- subscription disclosure (snippets/cart-details.liquid) --------------
     "Subscription included - you'll be charged automatically" must show on
     every cart surface whenever the cart holds a subscription line, including
     carts rebuilt client-side from templates (which render it hidden). Driven
     from the cart store and from each cart.js read below; re-applied in paint()
     so a re-rendered cart picks it up. */
  var hasSub = null;
  function setHasSub(cart) {
    if (!cart || !cart.items) { return; }
    hasSub = cart.items.some(function (i) { return !!i.selling_plan_allocation; });
    syncNotice();
  }
  function syncNotice() {
    if (hasSub === null) { return; }
    var want = hasSub ? '' : 'none';
    var ns = document.querySelectorAll('[data-os-sub-notice]');
    for (var i = 0; i < ns.length; i++) {
      if (ns[i].style.display !== want) { ns[i].style.display = want; }
    }
  }

  /* --- compute the nudges for the current cart ----------------------------- */
  function resolveUpsells() {
    return fetch(ROOT + 'cart.js', { credentials: 'same-origin' })
      .then(function (r) { if (!r.ok) { throw new Error('bad'); } return r.json(); })
      .then(function (cart) {
        setHasSub(cart);
        var items = (cart && cart.items) || [];
        return Promise.all(items.map(function (it) {
          /* Only one-time lines get nudged. A subscription line is left alone. */
          if (it.selling_plan_allocation) { return null; }
          return getProduct(it.handle).then(function (p) {
            if (!p) { return null; }
            var out = { key: it.key, qty: it.quantity, title: it.product_title || p.title };
            /* ONE cross-sell per line, chosen by eligibility (Lauren 2026-09-18):
                 - Subscription-eligible products ALWAYS cross-sell to their
                   subscription, never the 24-pack.
                 - Everything else cross-sells to the 24-pack.
               A subscription is the better lifetime outcome where it exists, so it
               wins outright; the bigger one-time basket is the fallback for the
               products that have no subscription twin. */
            var su = subUrlFor(out.title);
            if (su) {
              /* Swap target: the twin's variant of the SAME pack size, with the
                 plans that variant actually carries (twins require a plan). */
              out.sub = { url: su };
              var lineIs24 = is24(it.variant_title) || is24(it.product_title);
              return getProduct(su.split('/products/')[1]).then(function (tw) {
                var tv = null;
                ((tw && tw.variants) || []).forEach(function (v) {
                  if (!tv && v.available !== false && (lineIs24 ? is24(v.title) : is12(v.title))) { tv = v; }
                });
                var group = tw && (tw.selling_plan_groups || [])[0];
                var plans = [];
                if (tv && group) {
                  (group.selling_plans || []).forEach(function (sp) {
                    var a = (tv.selling_plan_allocations || []).filter(function (x) { return x.selling_plan_id === sp.id; })[0];
                    if (a) { plans.push({ id: sp.id, name: (sp.options && sp.options[0] && sp.options[0].value) || sp.name, price: a.price }); }
                  });
                }
                if (plans.length) {
                  var pct = Math.round((1 - plans[0].price / tv.price) * 100);
                  out.sub = { url: su, variantId: tv.id, plans: plans, pct: pct, oneTime: it.price, save: plans[0].price };
                }
                return out;
              });
            } else {
              /* 24-pack upgrade: only when the line is a SINGLE 12-pack and a
                 24-pack exists. Savings stated against the honest baseline of two
                 12-packs, and only shown when positive. Quantity 1 only (fixed
                 2026-09-20): the swap converts qty-for-qty, so 2 x 12-pack (already
                 24 fillets) became 2 x 24-pack - double the order under a "save $X"
                 label. For qty 2+ an honest swap would only lower the order. */
              var lineIs12 = is12(it.variant_title) || is12(it.product_title);
              if (lineIs12 && it.quantity === 1) {
                var v12price = it.price;                       /* per-unit 12-pack price, cents */
                var v24 = null;
                (p.variants || []).forEach(function (v) { if (!v24 && v.available !== false && is24(v.title)) { v24 = v; } });
                if (v24) {
                  var save = (2 * v12price) - v24.price;        /* vs two 12-packs */
                  if (save > 0) {
                    out.upgrade = { v24id: v24.id, v24price: v24.price, save: save };
                  }
                }
              }
            }
            return (out.upgrade || out.sub) ? out : null;
          });
        })).then(function (arr) {
          /* ONE nudge per cart (Lauren 2026-10-02: "S&S comes up so many times").
             Subscribe & Save wins; a 24-pack upgrade only shows when no line can
             subscribe. Cart order is kept, so it sits under the first eligible line. */
          arr = arr.filter(Boolean);
          var pick = arr.filter(function (u) { return u.sub; })[0] || arr.filter(function (u) { return u.upgrade; })[0];
          return pick ? [pick] : [];
        });
      })
      .catch(function () { return []; });
  }

  /* --- render -------------------------------------------------------------- */
  function rowsHtml(list) {
    var html = '';
    list.forEach(function (u) {
      if (u.upgrade) {
        html += '<div class="os-upsell__row">' +
          '<div class="os-upsell__text"><strong>Save ' + esc(money(u.upgrade.save)) + '</strong> — upgrade ' +
          esc(u.title) + ' to the 24-Pack' +
          '<span class="os-upsell__sub">24 fillets for ' + esc(money(u.upgrade.v24price)) + ', vs ' + esc(money(u.upgrade.v24price + u.upgrade.save)) + ' for two 12-Packs</span></div>' +
          '<button type="button" class="os-upsell__btn" data-os-upsell-upgrade ' +
          'data-key="' + esc(u.key) + '" data-v24="' + esc(u.upgrade.v24id) + '" data-qty="' + esc(u.qty) + '">Upgrade</button>' +
          '</div>';
      }
      if (u.sub && u.sub.plans) {
        var first = u.sub.plans[0];
        var opts = u.sub.plans.map(function (p) {
          return '<option value="' + esc(p.id) + '" data-price="' + esc(p.price) + '">' + esc(p.name) + '</option>';
        }).join('');
        html += '<div class="os-upsell__row">' +
          '<div class="os-upsell__text"><strong>Subscribe &amp; save' + (u.sub.pct > 0 ? ' ' + esc(u.sub.pct) + '%' : '') + '</strong> on ' + esc(u.title) +
          '<span class="os-upsell__sub" data-os-upsell-subprice>' + subPriceText(u, first.price) + '</span>' +
          '<select class="os-upsell__freq" data-os-upsell-plan aria-label="Delivery frequency" style="display:block;margin:6px 0 2px;min-height:36px;max-width:100%;padding:4px 8px;font:inherit;font-size:0.8rem;color:var(--brand-navy,#0f0a30);background:var(--brand-white,#fff);border:1px solid rgba(15,10,48,0.3);">' + opts + '</select>' +
          '<span class="os-upsell__sub">Replaces the one-time item · skip, swap or cancel anytime</span></div>' +
          '<button type="button" class="os-upsell__btn" data-os-upsell-sub ' +
          'data-key="' + esc(u.key) + '" data-variant="' + esc(u.sub.variantId) + '" data-qty="' + esc(u.qty) + '">Subscribe</button>' +
          '</div>';
      } else if (u.sub) {
        html += '<div class="os-upsell__row">' +
          '<div class="os-upsell__text"><strong>Subscribe &amp; save</strong> on ' + esc(u.title) +
          '<span class="os-upsell__sub">Skip, swap or cancel anytime</span></div>' +
          '<a class="os-upsell__btn os-upsell__btn--link" href="' + esc(u.sub.url) + '">Set it up</a>' +
          '</div>';
      }
    });
    return html;
  }

  /* "$182.16 per delivery (was $198)", with the quantity when it is more than one. */
  function subPriceText(u, price) {
    var q = u.qty > 1 ? u.qty + ' × ' : '';
    var was = u.sub.oneTime > price ? ' (was ' + money(u.sub.oneTime) + ')' : '';
    return q + money(price) + ' per delivery' + was;
  }

  function nudgeSig(u) {
    return (u.upgrade ? 'g' + u.upgrade.save : '') + (u.sub ? 's' + u.sub.save : '');
  }

  /* Each nudge is placed directly BELOW its own <cart-item>, not stacked in one
     box at the bottom, so the shopper sees the upgrade next to the product it is
     for. Line items are <cart-item key="variant:hash">, matched to the cart line
     key from cart.js. There can be two carts in the DOM (drawer + /cart page), so
     every matching line gets its nudge. <cart-items> re-renders on cart changes,
     which wipes the injected nudge — the MutationObserver + cart events re-run
     paint(), and the signature check below makes re-injection idempotent (no
     mutation loop). */
  function paint(list) {
    list = list || [];
    syncNotice();

    /* Clear the legacy single bottom box from the earlier version, if present. */
    var legacy = document.querySelectorAll('[data-os="cart-upsell"]');
    for (var L = 0; L < legacy.length; L++) {
      if (legacy[L].parentNode) { legacy[L].parentNode.removeChild(legacy[L]); }
    }

    /* What each cart line should show now, keyed by its line key. */
    var wanted = {};
    list.forEach(function (u) {
      var html = rowsHtml([u]);
      if (html) { wanted[u.key] = { html: html, sig: nudgeSig(u) }; }
    });

    /* Drop any existing per-line nudge that is now stale (line gone or changed). */
    var existing = document.querySelectorAll('[data-os="cart-upsell-line"]');
    for (var i = 0; i < existing.length; i++) {
      var n = existing[i];
      var k = n.getAttribute('data-os-key');
      if (!wanted[k] || n.getAttribute('data-os-sig') !== wanted[k].sig) {
        if (n.parentNode) { n.parentNode.removeChild(n); }
      }
    }

    /* FIXED 2026-10-02: when <cart-items> re-renders, old nudges could survive
       as orphans and a new one was added each time, so boxes stacked up. A
       nudge is only valid directly after its own <cart-item>; anything else goes. */
    var all = document.querySelectorAll('[data-os="cart-upsell-line"]');
    for (var d = 0; d < all.length; d++) {
      var nd = all[d], prev = nd.previousElementSibling;
      var ok = prev && prev.tagName === 'CART-ITEM' && prev.getAttribute('key') === nd.getAttribute('data-os-key');
      if (!ok && nd.parentNode) { nd.parentNode.removeChild(nd); }
    }

    /* Insert / keep one nudge immediately after each associated <cart-item>. */
    Object.keys(wanted).forEach(function (key) {
      var lines = document.querySelectorAll('cart-item[key="' + key + '"]');
      for (var m = 0; m < lines.length; m++) {
        var item = lines[m];
        var next = item.nextElementSibling;
        if (next && next.getAttribute && next.getAttribute('data-os') === 'cart-upsell-line') {
          if (next.getAttribute('data-os-key') === key && next.getAttribute('data-os-sig') === wanted[key].sig) {
            continue; /* already correct — leave it */
          }
          next.parentNode.removeChild(next); /* mismatched — replace */
        }
        var box = document.createElement('div');
        box.className = 'os-upsell os-upsell--line';
        box.setAttribute('data-os', 'cart-upsell-line');
        box.setAttribute('data-os-key', key);
        box.setAttribute('data-os-sig', wanted[key].sig);
        box.innerHTML = wanted[key].html;
        item.insertAdjacentElement('afterend', box);
      }
    });
  }

  /* --- the 24-pack swap ---------------------------------------------------- */
  function wait(ms) { return new Promise(function (res) { setTimeout(res, ms); }); }
  function rawChange(key, qty) {
    return fetch(ROOT + 'cart/change.js', {
      method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity: qty })
    }).then(function (r) { return r.json().catch(function () { return null; }); });
  }
  function rawAdd(id, qty, plan) {
    var item = { id: id, quantity: qty };
    if (plan) { item.selling_plan = plan; }
    return fetch(ROOT + 'cart/add.js', {
      method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: [item] })
    }).then(function (r) { return r.json().then(function (d) { if (!r.ok || d.status) { throw new Error(d.description || 'add failed'); } return d; }); });
  }
  /* Find the CURRENT key of the one-time line for this variant. Shopify re-hashes
     a line's key every time the cart is modified, so the key captured when the
     button rendered is stale the moment we add the 24-pack — using it 400s. We
     re-read it fresh. Matching on variant id AND "no selling plan" means we only
     ever remove the one-time line, never a subscription line of the same variant. */
  function freshOneTimeKey(variant12) {
    return fetch(ROOT + 'cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var items = (cart && cart.items) || [];
        for (var i = 0; i < items.length; i++) {
          if (String(items[i].variant_id) === String(variant12) && !items[i].selling_plan_allocation) { return items[i].key; }
        }
        return null;
      }).catch(function () { return null; });
  }
  /* Swap = add the 24-pack, let the add settle, re-read the (now re-hashed)
     12-pack key, then remove it. Add first so a failure never empties the line. */
  function upgrade(key, v24id, qty) {
    var variant12 = String(key).split(':')[0];
    return rawAdd(v24id, qty)
      .then(function () { return wait(150); })
      .then(function () { return freshOneTimeKey(variant12); })
      .then(function (freshKey) { if (freshKey) { return rawChange(freshKey, 0); } })
      .then(function () {
        return cartModule().then(function (C) {
          if (C && typeof C.refresh === 'function') { try { C.refresh(); } catch (e) {} }
          ['cart:updated', 'cart:refresh', 'cart:change'].forEach(function (ev) {
            try { document.dispatchEvent(new Event(ev)); } catch (e) {}
          });
        });
      });
  }

  function storeRefresh() {
    return cartModule().then(function (C) {
      if (C && typeof C.refresh === 'function') { try { C.refresh(); } catch (e) {} }
      ['cart:updated', 'cart:refresh', 'cart:change'].forEach(function (ev) {
        try { document.dispatchEvent(new Event(ev)); } catch (e) {}
      });
    });
  }

  /* Subscribe swap = add the twin on the chosen plan, then remove the one-time
     line (re-read by variant, as above). Add first so a failure never empties
     the cart; the store is refreshed either way so the drawer is never stale. */
  function subscribe(key, variantId, plan, qty) {
    var oneTimeVariant = String(key).split(':')[0];
    return rawAdd(variantId, qty, plan)
      .then(function () { return wait(150); })
      .then(function () { return freshOneTimeKey(oneTimeVariant); })
      .then(function (freshKey) { if (freshKey) { return rawChange(freshKey, 0); } })
      .then(storeRefresh, function (err) { return storeRefresh().then(function () { throw err; }); });
  }

  document.addEventListener('change', function (e) {
    var sel = e.target.closest ? e.target.closest('[data-os-upsell-plan]') : null;
    if (!sel) { return; }
    var row = sel.closest('.os-upsell__row');
    var btn = row && row.querySelector('[data-os-upsell-sub]');
    var out = row && row.querySelector('[data-os-upsell-subprice]');
    var opt = sel.options[sel.selectedIndex];
    var price = opt ? parseInt(opt.getAttribute('data-price'), 10) : NaN;
    var u = (last || []).filter(function (x) { return btn && x.key === btn.getAttribute('data-key'); })[0];
    if (out && u && u.sub && !isNaN(price)) { out.innerHTML = subPriceText(u, price); }
  });

  document.addEventListener('click', function (e) {
    var sbtn = e.target.closest ? e.target.closest('[data-os-upsell-sub]') : null;
    if (sbtn) {
      e.preventDefault();
      var skey = sbtn.getAttribute('data-key');
      var svar = parseInt(sbtn.getAttribute('data-variant'), 10);
      var sqty = parseInt(sbtn.getAttribute('data-qty'), 10) || 1;
      var srow = sbtn.closest('.os-upsell__row');
      var ssel = srow && srow.querySelector('[data-os-upsell-plan]');
      var splan = ssel ? parseInt(ssel.value, 10) : NaN;
      if (!skey || !svar || !splan || sbtn.getAttribute('data-busy')) { return; }
      sbtn.setAttribute('data-busy', '');
      sbtn.setAttribute('aria-busy', 'true');
      var slabel = sbtn.textContent;
      sbtn.textContent = 'Subscribing…';
      subscribe(skey, svar, splan, sqty).then(function () { refresh(); }).catch(function () {
        sbtn.removeAttribute('data-busy'); sbtn.removeAttribute('aria-busy'); sbtn.textContent = slabel;
        refresh();
      });
      return;
    }

    var btn = e.target.closest ? e.target.closest('[data-os-upsell-upgrade]') : null;
    if (!btn) { return; }
    e.preventDefault();
    var key = btn.getAttribute('data-key');
    var v24 = parseInt(btn.getAttribute('data-v24'), 10);
    var qty = parseInt(btn.getAttribute('data-qty'), 10) || 1;
    if (!key || !v24 || btn.getAttribute('data-busy')) { return; }
    btn.setAttribute('data-busy', '');
    btn.setAttribute('aria-busy', 'true');
    var label = btn.textContent;
    btn.textContent = 'Upgrading…';
    upgrade(key, v24, qty).then(function () { refresh(); }).catch(function () {
      btn.removeAttribute('data-busy'); btn.removeAttribute('aria-busy'); btn.textContent = label;
    });
  });

  /* --- lifecycle (mirror of the other cart scripts) ------------------------ */
  var token = 0, inflight = false, again = false, last = null;
  function refresh() {
    if (inflight) { again = true; return; }
    inflight = true; var mine = ++token;
    resolveUpsells().then(function (list) {
      inflight = false; if (mine !== token) { return; }
      last = list; try { paint(list); } catch (e) {}
      if (again) { again = false; refresh(); }
    }).catch(function () { inflight = false; last = null; try { paint(null); } catch (e) {} if (again) { again = false; refresh(); } });
  }

  var queued = false;
  function schedule() {
    if (queued) { return; }
    queued = true;
    var done = false;
    var run = function () { if (done) { return; } done = true; queued = false; try { if (last) { paint(last); } } catch (e) {} };
    (window.requestAnimationFrame || function (f) { setTimeout(f, 16); })(run);
    setTimeout(run, 250);
  }

  function boot() {
    schedule(); refresh();
    try { new MutationObserver(schedule).observe(document.documentElement, { childList: true, subtree: true }); } catch (e) {}
    /* on:cart:loaded is the cart store's own event (every Cart.add/change/refresh). */
    ['cart:updated', 'cart:refresh', 'cart:open', 'cart:change', 'cart:added', 'on:cart:loaded'].forEach(function (ev) {
      document.addEventListener(ev, function () { schedule(); refresh(); });
    });
    cartModule().then(function (C) {
      if (!C || typeof C.subscribe !== 'function') { return; }
      try {
        setHasSub(C.getState().cart);
        C.subscribe(function (s) { setHasSub(s && s.cart); });
      } catch (e) {}
    });
    setInterval(refresh, 300000);
  }
  if (document.readyState === 'loading') { document.addEventListener('DOMContentLoaded', boot); } else { boot(); }
})();
